<template>
    <div class="col-sm-12 editor-form">
        <h2 class="editor-header">{{ $t('subscription.edit_subscriptions') }}</h2>
        <p class="company-name">{{ subscriptionUpdate.company?.name }}</p>
        <div class="nopads">
            <ul class="nav nav-tabs routa-tabs" role="tablist">
                <li class="nav-item">
                    <button
                        class="routa-tab"
                        :class="{ active: tabIndex === 0 }"
                        :style="linkTabClass(0)"
                        @click="tabIndex = 0"
                    >
                        {{ $t('subscription.route_optimization') }}
                    </button>
                </li>
                <li class="nav-item">
                    <button
                        class="routa-tab"
                        :class="{ active: tabIndex === 1 }"
                        :style="linkTabClass(1)"
                        @click="tabIndex = 1"
                    >
                        {{ $t('subscription.chat') }}
                    </button>
                </li>
                <li class="nav-item">
                    <button
                        class="routa-tab"
                        :class="{ active: tabIndex === 2 }"
                        :style="linkTabClass(2)"
                        @click="tabIndex = 2"
                    >
                        {{ $t('subscription.dash_cam') }}
                    </button>
                </li>
            </ul>
            <div class="tab-content routa-tab-content">
                <!-- Route Optimization Tab -->
                <div v-show="tabIndex === 0" class="tab-pane fade show active">
                    <div class="col-sm-12 form-data nopads">
                        <span class="span-title">{{ $t('subscription.status') }}</span>
                        <select
                            v-model="subscriptionUpdate.routeOptimization.is_active"
                            class="form-select form-select-sm"
                        >
                            <option :value="true">{{ $t('subscription.active') }}</option>
                            <option :value="false">{{ $t('subscription.inactive') }}</option>
                        </select>

                        <div class="row">
                            <div class="col-sm-6 nopads">
                                <span class="span-title">{{ $t('subscription.start_date') }}</span>
                                <input
                                    v-model="subscriptionUpdate.routeOptimization.start_date"
                                    type="date"
                                    class="form-control form-control-sm"
                                />
                            </div>
                            <div class="col-sm-6 nopads">
                                <span class="span-title">{{ $t('subscription.end_date') }}</span>
                                <input
                                    v-model="subscriptionUpdate.routeOptimization.end_date"
                                    type="date"
                                    class="form-control form-control-sm"
                                />
                            </div>
                        </div>

                        <span class="span-title">{{ $t('subscription.notes') }}</span>
                        <textarea
                            v-model="subscriptionUpdate.routeOptimization.notes"
                            class="form-control"
                            rows="3"
                        />

                        <div v-if="subscriptionUpdate.routeOptimization.created_at">
                            <span class="span-title">{{ $t('subscription.created_at') }}</span>
                            <span class="tip-light">{{ formatDate(subscriptionUpdate.routeOptimization.created_at) }}</span>
                        </div>

                        <div v-if="subscriptionUpdate.routeOptimization.updated_at">
                            <span class="span-title">{{ $t('subscription.last_updated') }}</span>
                            <span class="tip-light">{{ formatDate(subscriptionUpdate.routeOptimization.updated_at) }}</span>
                        </div>

                        <div v-if="subscriptionUpdate.routeOptimization.last_modified_by">
                            <span class="span-title">{{ $t('subscription.last_modified_by') }}</span>
                            <span class="tip-light">{{ subscriptionUpdate.routeOptimization.last_modified_by.name }}</span>
                        </div>
                    </div>
                </div>

                <!-- Chat Tab -->
                <div v-show="tabIndex === 1" class="tab-pane fade show" :class="{ active: tabIndex === 1 }">
                    <div class="col-sm-12 form-data nopads">
                        <span class="span-title">{{ $t('subscription.status') }}</span>
                        <select
                            v-model="subscriptionUpdate.chat.is_active"
                            class="form-select form-select-sm"
                        >
                            <option :value="true">{{ $t('subscription.active') }}</option>
                            <option :value="false">{{ $t('subscription.inactive') }}</option>
                        </select>

                        <span class="span-title">{{ $t('subscription.chat_daily_token_limit') }}</span>
                        <input
                            v-model.number="subscriptionUpdate.chat.daily_token_limit"
                            type="number"
                            min="0"
                            class="form-control form-control-sm"
                        />
                        <span class="tip-light">{{ $t('subscription.chat_daily_token_limit_help') }}</span>

                        <div class="row">
                            <div class="col-sm-6 nopads">
                                <span class="span-title">{{ $t('subscription.start_date') }}</span>
                                <input
                                    v-model="subscriptionUpdate.chat.start_date"
                                    type="date"
                                    class="form-control form-control-sm"
                                />
                            </div>
                            <div class="col-sm-6 nopads">
                                <span class="span-title">{{ $t('subscription.end_date') }}</span>
                                <input
                                    v-model="subscriptionUpdate.chat.end_date"
                                    type="date"
                                    class="form-control form-control-sm"
                                />
                            </div>
                        </div>

                        <span class="span-title">{{ $t('subscription.notes') }}</span>
                        <textarea
                            v-model="subscriptionUpdate.chat.notes"
                            class="form-control"
                            rows="3"
                        />

                        <div v-if="subscriptionUpdate.chat.created_at">
                            <span class="span-title">{{ $t('subscription.created_at') }}</span>
                            <span class="tip-light">{{ formatDate(subscriptionUpdate.chat.created_at) }}</span>
                        </div>

                        <div v-if="subscriptionUpdate.chat.updated_at">
                            <span class="span-title">{{ $t('subscription.last_updated') }}</span>
                            <span class="tip-light">{{ formatDate(subscriptionUpdate.chat.updated_at) }}</span>
                        </div>

                        <div v-if="subscriptionUpdate.chat.last_modified_by">
                            <span class="span-title">{{ $t('subscription.last_modified_by') }}</span>
                            <span class="tip-light">{{ subscriptionUpdate.chat.last_modified_by.name }}</span>
                        </div>
                    </div>
                </div>

                <!-- Dashcam Tab -->
                <div v-show="tabIndex === 2" class="tab-pane fade show" :class="{ active: tabIndex === 2 }">
                    <div class="col-sm-12 form-data nopads">
                        <dashcam-bucket-provisioning
                            :company-id="subscriptionUpdate.company.id"
                            :dash-cam="subscriptionUpdate.dashCam"
                            :is-admin="isAdmin"
                            @provisioned="onDashcamProvisioned"
                            @refetch-required="refetchAfterPartialFailure"
                        />

                        <template v-if="isDashcamProvisioned">
                            <span class="span-title">{{ $t('subscription.status') }}</span>
                            <select
                                v-model="subscriptionUpdate.dashCam.is_active"
                                class="form-select form-select-sm"
                            >
                                <option :value="true">{{ $t('subscription.active') }}</option>
                                <option :value="false">{{ $t('subscription.inactive') }}</option>
                            </select>

                            <div class="row">
                                <div class="col-sm-6 nopads">
                                    <span class="span-title">{{ $t('subscription.start_date') }}</span>
                                    <input
                                        v-model="subscriptionUpdate.dashCam.start_date"
                                        type="date"
                                        class="form-control form-control-sm"
                                    />
                                </div>
                                <div class="col-sm-6 nopads">
                                    <span class="span-title">{{ $t('subscription.end_date') }}</span>
                                    <input
                                        v-model="subscriptionUpdate.dashCam.end_date"
                                        type="date"
                                        class="form-control form-control-sm"
                                    />
                                </div>
                            </div>

                            <span class="span-title">{{ $t('subscription.notes') }}</span>
                            <textarea
                                v-model="subscriptionUpdate.dashCam.notes"
                                class="form-control"
                                rows="3"
                            />

                            <div v-if="subscriptionUpdate.dashCam.created_at">
                                <span class="span-title">{{ $t('subscription.created_at') }}</span>
                                <span class="tip-light">{{ formatDate(subscriptionUpdate.dashCam.created_at) }}</span>
                            </div>

                            <div v-if="subscriptionUpdate.dashCam.updated_at">
                                <span class="span-title">{{ $t('subscription.last_updated') }}</span>
                                <span class="tip-light">{{ formatDate(subscriptionUpdate.dashCam.updated_at) }}</span>
                            </div>

                            <div v-if="subscriptionUpdate.dashCam.last_modified_by">
                                <span class="span-title">{{ $t('subscription.last_modified_by') }}</span>
                                <span class="tip-light">{{ subscriptionUpdate.dashCam.last_modified_by.name }}</span>
                            </div>
                        </template>
                        <div v-else class="tip-light">
                            {{ $t('subscription.dashcam_provisioning_required_hint') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Single save/cancel for all tabs -->
            <div class="col-sm-12 button-container">
                <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
                    {{ $t('common.cancel') }}
                </button>
                <button class="btn btn-success form-button" :disabled="loading" @click.stop="submitAll">
                    {{ $t('common.save') }}
                </button>
            </div>
        </div>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {inject} from 'vue'
import {restApi} from '../mixins/RestApiMixin'
import DashcamBucketProvisioning from './DashcamBucketProvisioning'

export default {
    name: 'SubscriptionEditor',
    components: {DashcamBucketProvisioning},
    mixins: [restApi],
    props: {
        subscriptionToEdit: {
            type: Object,
            default: null
        },
        isAdmin: {
            type: Boolean,
            default: false
        }
    },
    emits: ['close', 'close-and-update'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            tabIndex: 0,
            subscriptionUpdate: this.copySubscription(this.subscriptionToEdit)
        }
    },
    computed: {
        isDashcamProvisioned() {
            return this.subscriptionUpdate.dashCam?.s3_bucket != null
        }
    },
    methods: {
        copySubscription: function (subscription) {
            const copy = JSON.parse(JSON.stringify(subscription));

            // Normalize route_optimization block
            const ro = copy.route_optimization || {};
            copy.routeOptimization = {
                is_active: ro.is_active ?? false,
                start_date: ro.start_date ? ro.start_date.split('T')[0] : null,
                end_date: ro.end_date ? ro.end_date.split('T')[0] : null,
                notes: ro.notes ?? null,
                created_at: ro.created_at ?? null,
                updated_at: ro.updated_at ?? null,
                last_modified_by: ro.last_modified_by ?? null
            };

            // Normalize chat block
            const ch = copy.chat || {};
            copy.chat = {
                is_active: ch.is_active ?? false,
                daily_token_limit: ch.daily_token_limit ?? 100000,
                start_date: ch.start_date ? ch.start_date.split('T')[0] : null,
                end_date: ch.end_date ? ch.end_date.split('T')[0] : null,
                notes: ch.notes ?? null,
                created_at: ch.created_at ?? null,
                updated_at: ch.updated_at ?? null,
                last_modified_by: ch.last_modified_by ?? null
            };

            // Normalize dash_cam block
            const dc = copy.dash_cam || {};
            copy.dashCam = {
                is_active: dc.is_active ?? false,
                s3_bucket: dc.s3_bucket ?? null,
                archiving_period_months: dc.archiving_period_months ?? null,
                glacier_transition_month: dc.glacier_transition_month ?? null,
                start_date: dc.start_date ? dc.start_date.split('T')[0] : null,
                end_date: dc.end_date ? dc.end_date.split('T')[0] : null,
                notes: dc.notes ?? null,
                created_at: dc.created_at ?? null,
                updated_at: dc.updated_at ?? null,
                last_modified_by: dc.last_modified_by ?? null
            };

            return copy;
        },

        buildRouteOptimizationPayload: function () {
            const ro = this.subscriptionUpdate.routeOptimization;
            return {
                is_active: ro.is_active,
                start_date: ro.start_date || null,
                end_date: ro.end_date || null,
                notes: ro.notes || null
            };
        },

        buildChatPayload: function () {
            const ch = this.subscriptionUpdate.chat;
            return {
                is_active: ch.is_active,
                daily_token_limit: ch.daily_token_limit,
                start_date: ch.start_date || null,
                end_date: ch.end_date || null,
                notes: ch.notes || null
            };
        },

        buildDashCamPayload: function () {
            const dc = this.subscriptionUpdate.dashCam;
            // s3_bucket is intentionally omitted: it is write-once and provisioned exclusively via
            // the separate dashcamProvisionBucketUrl POST endpoint. Sending the already-provisioned
            // value back here would be treated by the backend as an attempt to set it again and
            // rejected with 409, on every save, regardless of which tab triggered it.
            return {
                is_active: dc.is_active,
                start_date: dc.start_date || null,
                end_date: dc.end_date || null,
                notes: dc.notes || null
            };
        },

        onDashcamProvisioned: function (dashCamFromResponse) {
            this.subscriptionUpdate.dashCam = {
                ...this.subscriptionUpdate.dashCam,
                ...dashCamFromResponse,
                start_date: dashCamFromResponse.start_date ? dashCamFromResponse.start_date.split('T')[0] : null,
                end_date: dashCamFromResponse.end_date ? dashCamFromResponse.end_date.split('T')[0] : null
            };
            // is_active is trusted as-is from the response — the backend derives it from the retention
            // settings, so re-deriving or overriding it here would risk drifting from what was just
            // provisioned. No restUpdate call is made: the POST response is already the source of truth.
        },

        submitAll: function () {
            this.loading = true;
            const payload = {
                route_optimization: this.buildRouteOptimizationPayload(),
                chat: this.buildChatPayload(),
                dash_cam: this.buildDashCamPayload()
            };
            this.restUpdate(
                this.subscriptionUrl + '/' + this.subscriptionUpdate.company.id,
                payload,
                this.success,
                this.fail
            );
        },

        success: function () {
            this.loading = false;
            this.showAlert(this.$t('common.alert_update_success'), 'success');
            this.$emit('close-and-update');
        },

        fail: function (errorResponse) {
            this.loading = false;
            if (errorResponse?.status === 403) {
                this.showAlert(this.$t('subscription.dashcam_access_denied'));
                return;
            }
            this.showAlert(this.$t('common.alert_update_failed'));
        },

        refetchAfterPartialFailure: function () {
            // Triggered when DashcamBucketProvisioning's own provision-bucket request hits a
            // DUPLICATE_BUCKET/DUPLICATE_ORGANIZATION conflict (a bucket already exists server-side
            // even though this editor's local subscriptionUpdate.dashCam still showed it as
            // unprovisioned). Re-fetch to pick up the real, already-provisioned dash_cam fields.
            this.restFetch(this.subscriptionUrl + '/all', (response) => {
                const fresh = response?.data?.find(r => r.company?.id === this.subscriptionUpdate.company.id);
                if (!fresh) return;
                const freshCopy = this.copySubscription(fresh);
                this.subscriptionUpdate.dashCam.s3_bucket = freshCopy.dashCam.s3_bucket;
                this.subscriptionUpdate.dashCam.archiving_period_months = freshCopy.dashCam.archiving_period_months;
                this.subscriptionUpdate.dashCam.glacier_transition_month = freshCopy.dashCam.glacier_transition_month;
                this.subscriptionUpdate.dashCam.created_at = freshCopy.dashCam.created_at;
                this.subscriptionUpdate.dashCam.updated_at = freshCopy.dashCam.updated_at;
                this.subscriptionUpdate.dashCam.last_modified_by = freshCopy.dashCam.last_modified_by;
            });
        },

        linkTabClass(index) {
            if (this.tabIndex === index) {
                return ['tab-title', 'tab-title-active']
            } else {
                return ['tab-title']
            }
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            return new Date(dateString).toLocaleString();
        }
    }
}
</script>

<style scoped>
.company-name {
    font-size: 16px;
    margin-bottom: 1em;
    color: #333;
    padding: 0 !important;
}

.button-container {
    margin-top: 1.5rem;
}
</style>
